import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@pondigitalsolutions/pon-component-library";

import {
  VacancyFormAnswerField,
  VacancyFormField,
} from "../../types/VacancyTypes";
import { readFileAsDataUrl } from "../../utils/fileToBase64";
import { useSubmitVacancyForm } from "@hooks/useSubmitVacancyForm";
import { Checkbox } from "./Checkbox";
import { Input } from "./Input";
import { Radio } from "./Radio";
import { Textarea } from "./Textarea";
import { UploadFile } from "./UploadFile";
import { LinkedUploadField } from "./LinkedUploadField";
import {
  REQUIRED_ERROR_KEY,
  PATTERN_ERROR_KEY,
  type ErrorKey,
} from "./ErrorMessage";

interface VacancyFormFactoryProps {
  formId: string;
  locale: string;
  formFields: VacancyFormField[];
  applyFormSubmitted: () => void;
  setFormError: (error: string | null) => void;
}

type FieldValue = string | boolean | File | null;
type LinkedFieldMode = "input" | "uploader";

const getLinkedUploaderByRef = (formFields: VacancyFormField[]) => {
  const linkedUploaderByRef = new Map<string, VacancyFormField>();

  formFields.forEach((field, index) => {
    if (field.type !== "uploader" || index === 0) return;

    const precedingField = formFields[index - 1];
    if (
      (precedingField.type === "input" || precedingField.type === "textarea") &&
      `${precedingField.ref}_file` === field.ref
    ) {
      linkedUploaderByRef.set(precedingField.ref, field);
    }
  });

  return linkedUploaderByRef;
};

const answerForField = (
  field: VacancyFormField,
  value: FieldValue
): Promise<string | boolean | null> => {
  switch (field.type) {
    case "uploader":
      return value instanceof File
        ? readFileAsDataUrl(value)
        : Promise.resolve(null);
    case "yes_no":
      return Promise.resolve(
        typeof value === "string" ? value === "Yes" : null
      );
    case "legal":
      return Promise.resolve(typeof value === "boolean" ? value : false);
    default:
      return Promise.resolve(typeof value === "string" ? value : null);
  }
};

export const VacancyFormFactory = ({
  formId,
  locale,
  formFields,
  applyFormSubmitted,
  setFormError,
}: VacancyFormFactoryProps) => {
  const { t } = useTranslation();
  const [values, setValues] = useState<Record<string, FieldValue>>({});
  const [errors, setErrors] = useState<Record<string, ErrorKey>>({});
  const [linkedFieldMode, setLinkedFieldMode] = useState<
    Record<string, LinkedFieldMode>
  >({});
  const { mutateAsync, isPending } = useSubmitVacancyForm();

  const linkedUploaderByRef = getLinkedUploaderByRef(formFields);
  const linkedUploaderRefs = new Set(
    Array.from(linkedUploaderByRef.values()).map((field) => field.ref)
  );

  const setValue = (ref: string, value: FieldValue) => {
    setValues((prev) => ({ ...prev, [ref]: value }));
  };

  const setLinkedMode = (
    textRef: string,
    uploaderRef: string,
    mode: LinkedFieldMode
  ) => {
    setLinkedFieldMode((prev) => ({ ...prev, [textRef]: mode }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[textRef];
      delete next[uploaderRef];
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Validate form fields
    const newErrors: Record<string, ErrorKey> = {};
    formFields.forEach((field) => {
      if (linkedUploaderRefs.has(field.ref)) return;

      const linkedUploader = linkedUploaderByRef.get(field.ref);
      const activeField =
        linkedUploader && linkedFieldMode[field.ref] === "uploader"
          ? linkedUploader
          : field;
      const value = values[activeField.ref];

      if (activeField.required && !value) {
        newErrors[activeField.ref] = REQUIRED_ERROR_KEY;
      } else if (
        activeField.type === "input" &&
        (activeField.ref === "email" || activeField.ref === "phone") &&
        activeField.pattern &&
        typeof value === "string" &&
        value !== "" &&
        !new RegExp(activeField.pattern).test(value)
      ) {
        newErrors[activeField.ref] = PATTERN_ERROR_KEY;
      }
    });

    // If there are validation errors, set them and return early
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const form: VacancyFormAnswerField[] = await Promise.all(
      formFields.map(async (field) => ({
        ref: field.ref,
        question: field.label,
        type: field.type,
        answer: await answerForField(field, values[field.ref] ?? null),
      }))
    );

    // Submit the form data
    await mutateAsync({ formId, locale, form })
      .then(() => {
        // Close the form after submission
        applyFormSubmitted();
      })
      .catch(() => {
        // Handle submission error
        setFormError(t("Vacancies.form.error.submitError"));
      });
  };

  return (
    <form
      className="flex flex-col gap-4"
      noValidate
      onSubmit={(e) => {
        void handleSubmit(e);
      }}
    >
      {formFields.map((field) => {
        switch (field.type) {
          case "input":
          case "textarea": {
            const linkedUploader = linkedUploaderByRef.get(field.ref);
            if (linkedUploader) {
              return (
                <LinkedUploadField
                  key={field.ref}
                  textField={field}
                  uploaderField={linkedUploader}
                  mode={linkedFieldMode[field.ref] ?? "input"}
                  onModeChange={(mode) =>
                    setLinkedMode(field.ref, linkedUploader.ref, mode)
                  }
                  textValue={(values[field.ref] as string) ?? ""}
                  onTextChange={(e) => setValue(field.ref, e.target.value)}
                  onFileChange={(e) =>
                    setValue(linkedUploader.ref, e.target.files?.[0] ?? null)
                  }
                  textError={errors[field.ref]}
                  fileError={errors[linkedUploader.ref]}
                />
              );
            }

            return field.type === "textarea" ? (
              <Textarea
                key={field.ref}
                label={field.label}
                required={field.required}
                id={field.ref}
                description={field.info}
                value={(values[field.ref] as string) ?? ""}
                onChange={(e) => setValue(field.ref, e.target.value)}
                size="md"
                error={errors[field.ref]}
              />
            ) : (
              <Input
                key={field.ref}
                label={field.label}
                required={field.required}
                id={field.ref}
                description={field.info}
                value={(values[field.ref] as string) ?? ""}
                onChange={(e) => setValue(field.ref, e.target.value)}
                size="md"
                error={errors[field.ref]}
              />
            );
          }
          case "uploader":
            if (linkedUploaderRefs.has(field.ref)) return null;

            return (
              <UploadFile
                key={field.ref}
                label={field.label}
                required={field.required}
                id={field.ref}
                description={field.info}
                allowedMimes={field.allowedMimes}
                onChange={(e) =>
                  setValue(field.ref, e.target.files?.[0] ?? null)
                }
                error={errors[field.ref]}
              />
            );
          case "single":
            return (
              <Radio
                key={field.ref}
                label={field.label}
                name={field.ref}
                options={field.options ?? []}
                required={field.required}
                id={field.ref}
                description={field.info}
                value={(values[field.ref] as string) ?? undefined}
                onChange={(e) => setValue(field.ref, e.target.value)}
                error={errors[field.ref]}
              />
            );
          case "yes_no":
            return (
              <Radio
                key={field.ref}
                label={field.label}
                name={field.ref}
                options={["Yes", "No"]}
                required={field.required}
                id={field.ref}
                description={field.info}
                value={(values[field.ref] as string) ?? undefined}
                onChange={(e) => setValue(field.ref, e.target.value)}
                error={errors[field.ref]}
              />
            );
          case "legal":
            return (
              <Checkbox
                key={field.ref}
                label="Akkoord"
                required={field.required}
                id={field.ref}
                description={field.info}
                checked={(values[field.ref] as boolean) ?? false}
                onChange={(e) => setValue(field.ref, e.target.checked)}
                error={errors[field.ref]}
              />
            );
          default:
            return null;
        }
      })}

      <Button
        id="vacancy-form-submit"
        type="submit"
        variant="primary"
        size="lg"
        disabled={isPending}
        className="w-fit"
      >
        {t("Vacancies.form.submit")}
      </Button>
    </form>
  );
};
